import React, { createContext, useContext, useEffect, useRef } from "react";

const keyMap = {
	esc: "Escape",
	enter: "Enter",
	tab: "Tab",
	space: " ",
	backspace: "Backspace",
	delete: "Delete",
	del: "Delete",
	up: "ArrowUp",
	down: "ArrowDown",
	left: "ArrowLeft",
	right: "ArrowRight",
};

function parseKeyCombo(key) {
	const parts = key.toLowerCase().split("+");
	const mainKey = parts.pop();
	const modifiers = {
		ctrl: parts.includes("ctrl"),
		meta: parts.includes("meta"),
		shift: parts.includes("shift"),
		alt: parts.includes("alt"),
	};
	return { mainKey, modifiers };
}

function matchesEvent(e, keyCombo) {
	const { mainKey, modifiers } = keyCombo;
	const mapped = keyMap[mainKey] || mainKey;

	const keyMatches =
		e.key === mapped || e.key.toLowerCase() === mainKey.toLowerCase();
	if (!keyMatches) return false;

	if (modifiers.ctrl && !e.ctrlKey) return false;
	if (modifiers.meta && !e.metaKey) return false;
	if (modifiers.shift && !e.shiftKey) return false;
	if (modifiers.alt && !e.altKey) return false;

	return true;
}

function isInputFocused() {
	const el = document.activeElement;
	const tag = el?.tagName;
	return (
		tag === "INPUT" ||
		tag === "TEXTAREA" ||
		tag === "SELECT" ||
		el?.isContentEditable ||
		el?.role === "combobox" ||
		el?.role === "listbox" ||
		el?.role === "textbox" ||
		!!window.getSelection()?.toString()
	);
}

const KeyboardEventContext = createContext(null);

export function KeyboardEventProvider({ children }) {
	const stackRef = useRef([]);

	useEffect(() => {
		const handler = (e) => {
			const stack = stackRef.current;
			for (let i = stack.length - 1; i >= 0; i--) {
				const entry = stack[i];
				for (const combo of entry.combos) {
					if (matchesEvent(e, combo.parsed)) {
						if (!entry.forceCloseDialog && isInputFocused()) {
							return;
						}
						entry.onKeyEvent(combo.original, e);
						return;
					}
				}
				// Dialog handlers act as a barrier — unmatched keys
				// must not fall through to handlers below
				if (entry.forceCloseDialog) {
					return;
				}
			}
		};

		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, []);

	return (
		<KeyboardEventContext.Provider value={stackRef}>
			{children}
		</KeyboardEventContext.Provider>
	);
}

export function useKeyboardHandler(handleKeys, onKeyEvent, options = {}) {
	const stackRef = useContext(KeyboardEventContext);
	const callbackRef = useRef(onKeyEvent);
	callbackRef.current = onKeyEvent;

	const keysString = JSON.stringify(handleKeys);
	const forceCloseDialog = options.forceCloseDialog || false;

	useEffect(() => {
		const combos = (handleKeys || []).map((key) => ({
			original: key,
			parsed: parseKeyCombo(key),
		}));

		const entry = {
			combos,
			onKeyEvent: (key, e) => callbackRef.current(key, e),
			forceCloseDialog,
		};

		stackRef.current.push(entry);

		return () => {
			const idx = stackRef.current.indexOf(entry);
			if (idx !== -1) {
				stackRef.current.splice(idx, 1);
			}
		};
	}, [keysString, forceCloseDialog]);
}
